import fiveg from "@/_assets/svgs/5g.svg";
import { Card } from "@/components/ui/card";
import { RadioGroupItem } from "@/components/ui/radio-group";
import { Select } from "@/components/ui/select";
import { Package } from "@/types/packages/data-only/DataOnlyCountryPackages";
import { ArrowUpRight, Calendar, Globe, InfinityIcon } from "lucide-react";
import Image from "next/image";
import PackageDetailModal from "./PackageDetailModal";

import { Badge } from "@/components/ui/badge";
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

interface PropsType {
  selectedPackageId: string;
  setSelectedPackageId: (value: string) => void;
  packageDetail: Package;
  index: number;
  countryName: string;
  countryFlag: string;
  packagesToSelect?: Package[];
}
function CountryPackageCard({
  selectedPackageId,
  setSelectedPackageId,
  packageDetail,
  index,
  countryName,
  countryFlag,
  packagesToSelect,
}: PropsType) {
  const t = useTranslations(`Shared.countryPackages.packageCard`);
  const [selectedPackage, setSelectedPackage] = useState(packageDetail);

  const {
    data_unit,
    data_quantity,
    package_validity,
    package_validity_unit,
    price,
    discounted_price,
    discount_percentage,
    highest_connectivity,
    original_price,
    id,
    package_category,
  } = selectedPackage;

  const [packageIdSelectedFromList, setPackageIdSelectedFromList] =
    useState(id);

  function handlePackageSelection(id: string) {
    setPackageIdSelectedFromList(id);
    setSelectedPackageId(id);
  }

  useEffect(
    function () {
      if (packagesToSelect && packagesToSelect.length > 0) {
        setPackageIdSelectedFromList(packagesToSelect[0].id);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageDetail?.id],
  );

  useEffect(
    function () {
      if (packageIdSelectedFromList && packagesToSelect) {
        const searchedPackage = packagesToSelect.find(
          (p) => p.id === packageIdSelectedFromList,
        );
        if (searchedPackage) {
          setSelectedPackage(searchedPackage);
        }
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageIdSelectedFromList, packagesToSelect?.length],
  );

  useEffect(
    function () {
      if (!packagesToSelect && packageDetail) {
        setSelectedPackage(packageDetail);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageDetail.id],
  );

  useEffect(
    function () {
      if (packagesToSelect?.some((p) => p.id === selectedPackageId)) {
        setPackageIdSelectedFromList(selectedPackageId);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPackageId],
  );

  const isCardSelected = packagesToSelect
    ? packagesToSelect.some((p) => p.id === selectedPackageId)
    : selectedPackageId === packageDetail.id;

  return (
    <Card
      className={`group relative rounded-md border-2 shadow-none transition-all hover:cursor-pointer hover:border-primary hover:shadow-xl ${isCardSelected && "border-primary shadow-xl"} flex h-full flex-col justify-between`}
      onClick={() => {
        setSelectedPackageId(selectedPackage.id);
        // scrollToAddToCartIfMobile();
      }}
    >
      {/* Best Choice Badge */}
      {/* <div className="flex items-center justify-center">
        {index === 1 && (
          <div className="absolute -top-7 flex w-[101.5%] items-center justify-center rounded-t-md bg-primary py-1">
            <p className="flex items-center justify-center gap-1 text-body-sm font-500 text-background">
              <Trophy size={15} />
              Best Choice
            </p>
          </div>
        )}
      </div> */}

      {/* Price Card  */}
      <div className="relative flex flex-col gap-[9px] p-3">
        {/* Discount Baddge  */}
        {packageDetail.discount_percentage > 0 && (
          <div
            className={`absolute right-0 top-0 w-max rounded-bl-sm rounded-tl-sm rounded-tr-sm bg-primary-accent p-1 px-4`}
          >
            <p className="text-center text-body-sm font-500 text-foreground">
              {t(`discount`)} {discount_percentage}%
            </p>
          </div>
        )}

        {packageDetail.discount_percentage <= 0 && package_category && (
          <div
            className={`absolute right-0 top-0 w-max rounded-bl-sm rounded-tl-sm rounded-tr-sm bg-primary-accent p-1 px-4`}
          >
            <p className="text-center text-body-sm font-500 text-foreground">
              {package_category}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between">
          {packagesToSelect && (
            <RadioGroupItem
              aria-label={"Select package"}
              value={packageDetail.id}
              checked={packagesToSelect.some((p) => p.id === selectedPackageId)}
            />
          )}

          {!packagesToSelect && (
            <RadioGroupItem
              aria-label={"Select package"}
              value={packageDetail.id}
            />
          )}

          {discount_percentage === 0 && highest_connectivity === "5G" && (
            <Image
              src={fiveg}
              alt="5G Supported"
              height={17}
              width={20}
              sizes="auto"
            />
          )}
        </div>

        <p className="text-body-md font-500 leading-[100%]">
          {data_quantity === -1 ? (
            <span className="flex items-center gap-1 text-primary">
              <InfinityIcon />
              {t(`unlimited`)}{" "}
              {data_unit && data_unit !== "Standard" ? data_unit : t(`data`)}
            </span>
          ) : (
            `${data_quantity} ${data_unit}`
          )}
        </p>

        {packagesToSelect && (
          <div>
            <p className="flex items-center text-body-md font-500">
              ${discount_percentage > 0 ? discounted_price : price}
              <span className="ms-1 text-xs text-muted-foreground">USD</span>
              {packageDetail.discounted_price > 0 && (
                <span className="ms-2 text-xs text-destructive line-through opacity-70 xl:text-body-sm">
                  ${original_price}
                </span>
              )}
            </p>
          </div>
        )}

        {!packagesToSelect && (
          <p className="flex items-center gap-1 text-xs leading-[100%] text-muted-foreground">
            <Calendar size={14} className="opacity-80" /> {package_validity}{" "}
            {package_validity_unit}
          </p>
        )}

        {packagesToSelect && (
          <div onClick={(e) => e.stopPropagation()}>
            <Select
              aria-label={"Select package duration"}
              value={packageIdSelectedFromList}
              defaultValue={packageIdSelectedFromList}
              onValueChange={handlePackageSelection}
            >
              <SelectTrigger
                className="-mt-0.5 h-8 max-w-[180px] bg-muted font-500 text-foreground"
                aria-label={"Select package duration"}
              >
                <SelectValue placeholder={t(`selectDays`)} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t(`days`)}</SelectLabel>
                  {packagesToSelect.map((p, index) => (
                    <SelectItem
                      aria-label={`${p.package_validity} ${p.package_validity_unit}`}
                      value={String(p.id)}
                      key={index}
                      suffix={
                        p.package_category && (
                          <Badge className="px-1 py-0">
                            {p.package_category}
                          </Badge>
                        )
                      }
                    >
                      {p.package_validity} {p.package_validity_unit}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}

        {!packagesToSelect && (
          <p className="flex items-center text-body-md font-500">
            ${discount_percentage > 0 ? discounted_price : price}
            <span className="ms-1 text-xs text-muted-foreground">USD</span>
            {packageDetail.discounted_price > 0 && (
              <span className="ms-2 text-xs text-destructive line-through opacity-70 xl:text-body-sm">
                ${original_price}
              </span>
            )}
          </p>
        )}

        <p className="flex items-center gap-1 text-xs leading-[100%] text-muted-foreground">
          <Globe size={14} className="opacity-80" />
          <span className="flex items-center gap-1">
            {/* {countries.length > 1 &&
              countries
                .filter((item) => item.name === countryName)
                .map((item, index) => (
                  <Image
                    key={index}
                    src={item.image_url}
                    height={10}
                    width={16}
                    alt={`${item.name} flag`}
                    sizes="auto"
                    className="rounded-[2px] object-cover shadow-md"
                  />
                ))}

            {countries.length > 1 &&
              countries
                .filter((item) => item.name !== countryName)
                .slice(0, 3)
                .map((item, index) => (
                  <Image
                    key={index}
                    src={item.image_url}
                    height={10}
                    width={16}
                    alt="japan"
                    sizes="auto"
                    className="rounded-[2px] object-cover shadow-md"
                  />
                ))} */}
            {countryName}
            {/* {countries.slice(3).length > 1 && (
              <span className="ml-0.5 rounded-full text-xs">
                +{countries.slice(3).length}
              </span>
            )} */}
          </span>
        </p>
      </div>
      <div className="pb-3 ps-3.5">
        <PackageDetailModal
          packageDetail={selectedPackage}
          countryFlag={countryFlag}
          index={index}
        >
          {t("viewDetail")}
          <ArrowUpRight
            size={19}
            className="transition-transform duration-300 group-hover:rotate-45"
          />
        </PackageDetailModal>
      </div>
    </Card>
  );
}

export default CountryPackageCard;
